// Shared team/crew assignment helpers — used by both EditBookingModal (Schedule)
// and the manifest detail sheet (HomeActivity) so the role→member logic lives in
// exactly one place.

export const timeToMinutes = (time) => {
  if (!time) return 0;
  const [hours, minutes] = String(time).split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

export const checkTimeOverlap = (start1, dur1, start2, dur2) => {
  const s1 = timeToMinutes(start1);
  const e1 = s1 + (dur1 || 0);
  const s2 = timeToMinutes(start2);
  const e2 = s2 + (dur2 || 0);
  return s1 < e2 && e1 > s2;
};

// Build empty crew slots from an asset's team requirements (one slot per required
// head, honouring `quantity` for multi-crew roles). Supports legacy { roleId } and
// new { roleIds: [] } shapes.
export const buildTeamSlotsFromAsset = (asset, roles = []) => {
  if (!asset?.teamRequirements?.length) return [];
  const slots = [];
  asset.teamRequirements.forEach((req) => {
    const roleIds = req.roleIds || (req.roleId ? [req.roleId] : []);
    const roleName =
      roleIds.map((id) => roles.find((r) => r.id === id)?.name).filter(Boolean).join(' or ') ||
      req.roleName ||
      'Team';
    const count = Number(req.quantity) || 1;
    for (let i = 0; i < count; i++) {
      slots.push({ teamId: '', teamName: '', roleIds, roleId: roleIds[0] || '', roleName });
    }
  });
  return slots;
};

// Resolve the crew slots to render for a booking: use its saved assignments when
// present (normalised), otherwise template from the assigned asset's requirements.
export const resolveTeamSlots = (booking, asset, roles = []) => {
  const existing = booking?.teamAssignments || booking?.team_assignments || [];
  if (existing.length > 0) {
    return existing.map((a) => {
      const roleIds = a.roleIds || (a.roleId ? [a.roleId] : []);
      const roleName =
        a.roleName ||
        roleIds.map((id) => roles.find((r) => r.id === id)?.name).filter(Boolean).join(' or ') ||
        'Team Member';
      return { ...a, roleIds, roleId: a.roleId || roleIds[0] || '', roleName };
    });
  }
  return buildTeamSlotsFromAsset(asset, roles);
};

// Team members annotated with availability for a given role + trip window.
// Members with a different role, or already booked in an overlapping trip, are
// flagged unavailable (with a reason) rather than removed.
export const getTeamMembersWithStatus = ({
  teamMembers = [],
  roles = [],
  roleIds,
  date,
  startTime,
  durationMinutes = 0,
  allBookings = [],
  currentBookingId,
}) => {
  const ids = Array.isArray(roleIds) ? roleIds : roleIds ? [roleIds] : [];
  return teamMembers.map((c) => {
    let available = true;
    let reason = '';

    if (ids.length > 0 && !ids.includes(c.roleId)) {
      available = false;
      reason = roles.find((r) => r.id === c.roleId)?.name || 'Different role';
    }

    if (available && startTime && date) {
      const conflicts = allBookings.filter((t) => {
        const tDate = t.date || t.bookingDate;
        const tDur = t.duration || t.durationMinutes || 0;
        const tAssigns = t.teamAssignments || t.team_assignments || [];
        return (
          t.id !== currentBookingId &&
          tDate === date &&
          tAssigns.some((a) => a.teamId === c.id) &&
          checkTimeOverlap(startTime, durationMinutes, t.startTime, tDur)
        );
      });
      if (conflicts.length > 0) {
        available = false;
        reason = `Booked ${conflicts[0].startTime}`;
      }
    }

    return { ...c, available, reason };
  });
};

// First name + last initial ("John D.") — disambiguates same first names while
// staying compact for manifest chips.
export const shortCrewName = (fullName) => {
  if (!fullName) return '';
  const parts = String(fullName).trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

// Assigned crew (teamId set) for a booking, resolved to display names. Prefers a
// live teamMembers lookup, falling back to the name stored on the assignment.
export const getAssignedCrew = (booking, teamMembers = []) => {
  const assigns = booking?.teamAssignments || booking?.team_assignments || [];
  return assigns
    .filter((a) => a.teamId && a.teamId !== 'unassigned')
    .map((a) => {
      const member = teamMembers.find((m) => m.id === a.teamId);
      return {
        id: a.teamId,
        name: member?.name || a.teamName || 'Crew',
        roleName: a.roleName || '',
      };
    });
};
